/*
 * Nono-kartet — one department at a time. Today it maps ONLY the social-media
 * department: every recurring job from planning a week of posts to learning
 * from how they did, how far it runs on its own, and what it depends on. The
 * other departments come back one by one as each is built to the same bar.
 *
 * The six stages ARE the loop: Plan → Lage → Sjekke → Godkjenne → Publisere →
 * Lære, and Lære feeds the next Plan — which is why the wheel view fits.
 *
 * Hand-curated from the code (snapshot, not live). The lines drawn are the REAL
 * prerequisites in `deps` — a job that reads another job's output. Anything
 * with no deps reads straight from the hub (the content brain).
 *
 * Levels:
 *   auto     — runs by itself; a human only audits
 *   assisted — the system prepares, a named human decides/sends
 *   manual   — a person does it by hand (or must remember to look)
 *   missing  — nobody does it today
 */

import type { Locale } from '@/lib/i18n';

export type Level = 'auto' | 'assisted' | 'manual' | 'missing';

export type SystemId =
  | 'momence'
  | 'meta'
  | 'shopify'
  | 'stripe'
  | 'railway'
  | 'ads'
  | 'slack'
  | 'email'
  | 'clarity'
  | 'staff'
  | 'claude'
  | 'library';

type Text = Record<Locale, string>;

export interface Stage {
  id: string;
  name: Text;
}

export interface Job {
  id: string;
  stage: string;
  level: Level;
  name: Text;
  /** What actually happens today, in one or two sentences. */
  today: Text;
  /** The next concrete step — required for missing/manual, optional otherwise. */
  next?: Text;
  systems: SystemId[];
  /** Repo paths (or vault notes) that prove the status. */
  evidence: string[];
  /** Real prerequisites: jobs whose output this job reads. */
  deps: string[];
}

/** As-of date for the snapshot (shown on the page). */
export const MAP_AS_OF = '2026-10-19';

/** Loop order = clockwise order on the wheel, left→right on the line. */
export const STAGES: Stage[] = [
  { id: 'plan', name: { nb: 'Planlegge', en: 'Plan' } },
  { id: 'lage', name: { nb: 'Lage', en: 'Make' } },
  { id: 'sjekke', name: { nb: 'Sjekke', en: 'Check' } },
  { id: 'godkjenne', name: { nb: 'Godkjenne', en: 'Approve' } },
  { id: 'publisere', name: { nb: 'Publisere', en: 'Publish' } },
  { id: 'laere', name: { nb: 'Lære', en: 'Learn' } },
];

export const HUB = {
  name: { nb: 'Innholdshjernen', en: 'Content brain' } as Text,
  today: {
    nb: 'Det agentene vet: bildebiblioteket (2 125 filer med samtykke, KI-status og beskrivelse), tallene for hvert innlegg de siste 90 dagene, og Nonos regler for språk, jus og helsepåstander. Jobber uten egne avhengigheter leser rett herfra.',
    en: 'What the agents know: the photo library (2,125 files with consent, AI status and description), the numbers for every post in the last 90 days, and Nono’s rules for language, law and health claims. Jobs with no dependencies of their own read straight from here.',
  } as Text,
  systems: ['library', 'meta', 'railway', 'claude'] as SystemId[],
  evidence: [
    'apps/api/src/social/library.ts',
    'apps/api/src/social/insights.ts',
    'apps/api/src/social/guides.ts',
  ],
};

export const JOBS: Job[] = [
  /* ── PLANLEGGE ───────────────────────────────────────────── */
  {
    id: 'p_editor',
    stage: 'plan',
    level: 'assisted',
    name: { nb: 'Ukeplan (redaktøren)', en: 'Weekly plan (the editor)' },
    today: {
      nb: 'Redaktør-agenten leser tallene fra de siste 90 dagene og de siste innleggene, og lager en ukeplan: hvilke temaer, hvilket format og hvilken dag. Planen blir til utkast i køen – ingenting postes uten at et menneske sier ja.',
      en: 'The editor agent reads the last 90 days of numbers and the latest posts, and makes a weekly plan: which topics, which format, which day. The plan becomes drafts in the queue — nothing is posted until a human says yes.',
    },
    systems: ['claude', 'railway'],
    evidence: ['apps/api/src/social/agents.ts'],
    deps: ['l_insights'],
  },
  {
    id: 'p_calendar',
    stage: 'plan',
    level: 'missing',
    name: { nb: 'Kalender og anledninger', en: 'Calendar and occasions' },
    today: {
      nb: 'Planen vet ikke om ledige prøvetimer, høytider eller hva som skjer på senteret. Det må et menneske legge til i utkastet.',
      en: 'The plan does not know about open trial slots, holidays or what is happening at the centre. A human has to add that to the draft.',
    },
    next: {
      nb: 'La redaktøren lese ledige prøvetimer fra bookingreglene, så den kan dytte på dager med mye plass.',
      en: 'Let the editor read open trial slots from the booking rules, so it can push on days with lots of room.',
    },
    systems: ['claude', 'railway'],
    evidence: ['apps/api/src/booking/index.ts'],
    deps: ['p_editor'],
  },

  /* ── LAGE ────────────────────────────────────────────────── */
  {
    id: 'c_library',
    stage: 'lage',
    level: 'auto',
    name: { nb: 'Bibliotek-søk med samtykke', en: 'Library search with consent' },
    today: {
      nb: 'Agentene søker i bildebiblioteket etter tema, person og type. Søket viser bare filer der samtykke er avklart, og merker KI-bilder og retusjerte bilder som må ha «Retusjert reklame».',
      en: 'The agents search the photo library by topic, person and type. The search only returns files with cleared consent, and flags AI and retouched images that need the «Retusjert reklame» label.',
    },
    systems: ['library', 'railway'],
    evidence: ['apps/api/src/social/library.ts'],
    deps: [],
  },
  {
    id: 'c_specialists',
    stage: 'lage',
    level: 'assisted',
    name: { nb: 'Fagagenter per tema', en: 'Specialist agents per topic' },
    today: {
      nb: 'Fem agenter, hver ekspert på sitt: medlemshistorier, vertskapet, trening forklart, fellesskap og kaffe, og prøvetime-tilbud. Hver skriver tekst, velger filer fra biblioteket og beskriver det som mangler.',
      en: 'Five agents, each expert in one thing: member stories, the hosts, training explained, community and coffee, and the trial offer. Each writes the caption, picks files from the library and describes anything missing.',
    },
    systems: ['claude', 'library'],
    evidence: ['apps/api/src/social/agents.ts', 'apps/api/src/social/guides.ts'],
    deps: ['p_editor', 'c_library'],
  },
  {
    id: 'c_visuals',
    stage: 'lage',
    level: 'missing',
    name: { nb: 'Nytt visuelt innhold', en: 'New visuals' },
    today: {
      nb: 'Når biblioteket ikke har det som trengs, skriver agenten bare en beskrivelse. Ingen lager bildet eller filmen.',
      en: 'When the library lacks what is needed, the agent only writes a description. Nobody makes the image or film.',
    },
    next: {
      nb: 'Faste maler (tekst på bilde, karusell) som fylles automatisk, og registrering av alt generert i KI-registeret.',
      en: 'Fixed templates (text on image, carousel) filled automatically, with everything generated logged in the AI register.',
    },
    systems: ['claude', 'library'],
    evidence: [],
    deps: ['c_specialists'],
  },

  /* ── SJEKKE ──────────────────────────────────────────────── */
  {
    id: 'r_reviewer',
    stage: 'sjekke',
    level: 'assisted',
    name: { nb: 'Kvalitets- og jussjekk', en: 'Quality and legal check' },
    today: {
      nb: 'En egen agent, som ikke skrev utkastet, sjekker det mot Nonos stemme, helsepåstander, samtykke, merking og gjentakelser. Feiler det, får fagagenten én ny sjanse; feiler det igjen, havner det merket i køen.',
      en: 'A separate agent, who did not write the draft, checks it against Nono’s voice, health claims, consent, labelling and repetition. If it fails, the specialist gets one more try; if it fails again, it lands in the queue flagged.',
    },
    systems: ['claude'],
    evidence: ['apps/api/src/social/agents.ts', 'apps/api/src/social/guides.ts'],
    deps: ['c_specialists'],
  },

  /* ── GODKJENNE ───────────────────────────────────────────── */
  {
    id: 'g_queue',
    stage: 'godkjenne',
    level: 'assisted',
    name: { nb: 'Godkjenningskø', en: 'Approval queue' },
    today: {
      nb: 'Alle utkast ligger under Innhold i ads.nono.no. Et menneske godkjenner, retter eller avviser med en grunn. Rettelser og grunner lagres – de er det agentene skal lære av.',
      en: 'Every draft sits under Content in ads.nono.no. A human approves, edits or rejects with a reason. Edits and reasons are saved — they are what the agents will learn from.',
    },
    systems: ['ads', 'staff'],
    evidence: ['apps/web/src/features/innhold/InnholdPage.tsx', 'apps/api/src/dashboard/social.ts'],
    deps: ['r_reviewer'],
  },
  {
    id: 'g_autopilot',
    stage: 'godkjenne',
    level: 'missing',
    name: { nb: 'Autopilot per tema', en: 'Autopilot per topic' },
    today: {
      nb: 'Alt må godkjennes for hånd. Det er riktig nå.',
      en: 'Everything must be approved by hand. That is right for now.',
    },
    next: {
      nb: 'Når et tema har fått mange godkjente utkast på rad uten rettelser, kan det postes uten å vente – med mulighet til å stoppe.',
      en: 'Once a topic has had many approved drafts in a row without edits, it can post without waiting — with a way to stop it.',
    },
    systems: ['ads', 'railway'],
    evidence: [],
    deps: ['g_queue', 'u_publish'],
  },

  /* ── PUBLISERE ───────────────────────────────────────────── */
  {
    id: 'u_media',
    stage: 'publisere',
    level: 'missing',
    name: { nb: 'Filer på en offentlig lenke', en: 'Files on a public link' },
    today: {
      nb: 'Biblioteket ligger på en lokal disk. Meta henter bilder og film fra en offentlig lenke, så valgte filer må lastes opp først.',
      en: 'The library lives on a local disk. Meta fetches images and video from a public link, so chosen files must be uploaded first.',
    },
    next: {
      nb: 'Last opp valgte filer til Shopify Files når et utkast godkjennes.',
      en: 'Upload chosen files to Shopify Files when a draft is approved.',
    },
    systems: ['shopify', 'library'],
    evidence: ['medlemshistorier/cdn-lenker.json'],
    deps: ['g_queue'],
  },
  {
    id: 'u_publish',
    stage: 'publisere',
    level: 'missing',
    name: { nb: 'Posting på tidsplan', en: 'Posting on schedule' },
    today: {
      nb: 'Godkjente utkast postes fortsatt for hånd. Tokenet har allerede lov til å poste på Instagram og Facebook.',
      en: 'Approved drafts are still posted by hand. The token already has permission to post on Instagram and Facebook.',
    },
    next: {
      nb: 'En publiserer (vanlig kode, ikke en agent) som poster godkjente utkast til planlagt tid og lagrer innleggs-id-en.',
      en: 'A publisher (plain code, not an agent) that posts approved drafts at the planned time and stores the post id.',
    },
    systems: ['meta', 'railway'],
    evidence: [],
    deps: ['g_queue', 'u_media'],
  },

  /* ── LÆRE ────────────────────────────────────────────────── */
  {
    id: 'l_insights',
    stage: 'laere',
    level: 'auto',
    name: { nb: 'Tall for hvert innlegg', en: 'Numbers for every post' },
    today: {
      nb: 'Hver natt kl. 04:30 leses alle Instagram- og Facebook-innlegg fra de siste 90 dagene: rekkevidde, visninger, lagringer, delinger, profilbesøk og seertid. Ett øyeblikksbilde per dag, så innlegg kan sammenlignes på samme alder.',
      en: 'Every night at 04:30 every Instagram and Facebook post from the last 90 days is read: reach, views, saves, shares, profile visits and watch time. One snapshot per day, so posts can be compared at the same age.',
    },
    systems: ['meta', 'railway'],
    evidence: ['apps/api/src/social/insights.ts', 'apps/api/src/cron.ts'],
    deps: [],
  },
  {
    id: 'l_playbook',
    stage: 'laere',
    level: 'missing',
    name: { nb: 'Lærdommer (playbook)', en: 'Lessons (playbook)' },
    today: {
      nb: 'Tallene og rettelsene lagres, men ingen skriver ned hva de betyr. Redaktøren ser bare rå tall.',
      en: 'The numbers and edits are saved, but nobody writes down what they mean. The editor only sees raw numbers.',
    },
    next: {
      nb: 'En analyse-agent som hver uke skriver lærdommer med bevis (antall innlegg, mot snittet), og som redaktøren leser før neste plan.',
      en: 'An analyst agent that writes lessons with evidence (number of posts, versus the median) every week, which the editor reads before the next plan.',
    },
    systems: ['claude', 'railway'],
    evidence: [],
    deps: ['l_insights', 'g_queue'],
  },
  {
    id: 'l_bookings',
    stage: 'laere',
    level: 'missing',
    name: { nb: 'Innlegg → bookinger', en: 'Posts → bookings' },
    today: {
      nb: 'Vi vet hvor mange som så et innlegg, men ikke om noen booket prøvetime etter det.',
      en: 'We know how many saw a post, but not whether anyone booked a trial after it.',
    },
    next: {
      nb: 'Merk lenker i bio og historier med utm_source=instagram og innleggets id. Sporingen som finnes i dag fanger det opp av seg selv.',
      en: 'Tag links in the bio and stories with utm_source=instagram and the post id. The tracking that exists today picks it up by itself.',
    },
    systems: ['meta', 'railway'],
    evidence: ['apps/api/src/attribution/ad-params.ts'],
    deps: ['u_publish'],
  },
];

/**
 * Build order for the next jobs to build in this department (2026-10-19).
 * Publishing needs the files online first; lessons need a few weeks of queue
 * decisions before they say anything.
 */
export const BUILD_ORDER: string[] = ['u_media', 'u_publish', 'l_playbook', 'l_bookings', 'p_calendar'];
